import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useLocalRoutinePersistence } from './useLocalRoutinePersistence';
import { useUserData } from './useUserData';


export const useAutoSync = () => {
    const { user } = useUserData();
    const location = useLocation();
    const { getAllDirtyRoutines, clearLocal } = useLocalRoutinePersistence();


    // Inactivity Timer
    const lastActivityRef = useRef(0);
    useEffect(() => { lastActivityRef.current = Date.now(); }, []);
    const syncIntervalRef = useRef(null);

    // Sync Function
    const syncAllPending = async (silent = true) => {
        if (!user) return;

        const dirtyRoutines = getAllDirtyRoutines();
        if (dirtyRoutines.length === 0) return;

        // console.log('[AutoSync] Found dirty routines:', dirtyRoutines.map(r => r.id));

        const sanitize = (obj) => {
            if (obj === undefined || obj === null) return null;
            if (typeof obj !== 'object') return obj;
            if (Array.isArray(obj)) return obj.map(sanitize);
            const cleaned = {};
            Object.keys(obj).forEach(key => {
                const val = sanitize(obj[key]);
                if (val !== undefined && val !== null) cleaned[key] = val;
            });
            return cleaned;
        };

        let syncedCount = 0;

        for (const routine of dirtyRoutines) {
            try {
                // We trust the local data is the absolute latest version
                const cleanData = sanitize(routine.data);

                await updateDoc(doc(db, 'routines', routine.id), {
                    ...cleanData,
                    updatedAt: new Date().toISOString()
                });

                // Clear from local storage only after successful sync
                clearLocal(routine.id);
                syncedCount++;
            } catch (err) {
                console.error(`[AutoSync] Failed to sync routine ${routine.id}:`, err);
            }
        }

        if (syncedCount > 0 && !silent) {
            // Optional cleanup or notification
            // console.log(`Synced ${syncedCount} routines`);
        }
    };

    // 1. Sync on Route Change (Navigating between views)
    useEffect(() => {
        syncAllPending(true); // Silent sync on navigation
    }, [location.pathname]); // Trigger on path change

    // 2. Sync on App Load (Mount)
    useEffect(() => {
        const initSync = async () => {
            // Small delay to ensure auth is ready
            if (user) await syncAllPending(true);
        };
        initSync();
    }, [user]);

    // 3. Inactivity Logic
    useEffect(() => {
        const updateActivity = () => {
            lastActivityRef.current = Date.now();
        };

        // Events to track activity
        window.addEventListener('mousemove', updateActivity);
        window.addEventListener('keydown', updateActivity);
        window.addEventListener('click', updateActivity);
        window.addEventListener('touchstart', updateActivity);

        // Check for inactivity every minute
        syncIntervalRef.current = setInterval(() => {
            const now = Date.now();
            const inactiveTime = now - lastActivityRef.current;
            const FIFTEEN_MINUTES = 15 * 60 * 1000;

            if (inactiveTime > FIFTEEN_MINUTES) {
                syncAllPending(true);
                // Reset activity slightly so we don't spam sync every minute after 15m
                // (though syncAllPending checks for dirty, so it's safe if already synced)
            }
        }, 60 * 1000); // Check every minute

        return () => {
            window.removeEventListener('mousemove', updateActivity);
            window.removeEventListener('keydown', updateActivity);
            window.removeEventListener('click', updateActivity);
            window.removeEventListener('touchstart', updateActivity);
            if (syncIntervalRef.current) clearInterval(syncIntervalRef.current);
        };
    }, [user]); // Re-bind if user changes

    return null; // This is a logic-only hook/component
};
